import { DeleteOutline } from '@mui/icons-material';
import {
    Box,
    Button,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Vendor } from 'entities/Vendors';
import SellerIcon from 'shared/assets/svg/SellerIcon.svg';
import ServiceIcon from 'shared/assets/svg/ServiceIcon.svg';
import { formatPhoneNumber } from 'shared/lib/formatPhoneNumber/formatPhoneNumber';
import { Image } from 'widgets/Image';

import { VendorAccountStatusPill } from '../VendorAccountStatusPill/VendorAccountStatusPill';

export const VendorsTable = ({
    vendorsList,
    isLoadingVendors,
    isMainVendorsPage,
    onClickRow,
    onDeleteVendor,
    deleteVendorId,
}: {
    vendorsList: Vendor[];
    isLoadingVendors: boolean;
    isMainVendorsPage?: boolean;
    onClickRow: (arg0: string | Vendor) => void;
    onDeleteVendor?: (arg0: Vendor) => void | null;
    deleteVendorId?: string;
}) => {
    const { t } = useTranslation();

    return (
        <TableContainer>
            <Table
                data-testid="vendors-table"
                sx={{
                    marginTop: '30px',
                    '& td': {
                        padding: '6px 16px',
                        borderBottom: 'none',
                        height: '50px',
                    },
                    '& tr': {
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    },
                    '& th': {
                        paddingTop: '0px',
                        paddingBottom: '10px',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell></TableCell>
                        <TableCell align="left" data-testid="vendor-name-label">
                            <Typography variant="caption" fontWeight={600}>
                                {t('Name')}
                            </Typography>
                        </TableCell>
                        <TableCell
                            align="left"
                            data-testid="vendor-specialties-label"
                        >
                            <Typography variant="caption" fontWeight={600}>
                                {t('Specialties')}
                            </Typography>
                        </TableCell>
                        {isMainVendorsPage ? (
                            <>
                                <TableCell
                                    align="left"
                                    data-testid="vendor-primary-contact-label"
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                    >
                                        {t('Primary Contact')}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    data-testid="vendor-phone-email-label"
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                    >
                                        {t('Phone / Email')}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    data-testid="vendor-product-ordering-label"
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                    >
                                        {t('Product Ordering')}
                                    </Typography>
                                </TableCell>
                            </>
                        ) : (
                            <TableCell
                                align="left"
                                data-testid="vendor-status-label"
                            >
                                <Typography variant="caption" fontWeight={600}>
                                    {t('Added to property')}
                                </Typography>
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!isLoadingVendors &&
                        vendorsList?.map((row) => (
                            <TableRow
                                data-testclass="vendor-row"
                                key={row.id}
                                onClick={
                                    isMainVendorsPage
                                        ? () => onClickRow(row.id)
                                        : () =>
                                              !row.isAddToProperty &&
                                              onClickRow(row)
                                }
                                sx={{
                                    cursor: isMainVendorsPage
                                        ? 'pointer'
                                        : !row.isAddToProperty
                                        ? 'pointer'
                                        : 'default',
                                    opacity: row.isAddToProperty ? 0.3 : 1,
                                    '&:hover': {
                                        backgroundColor: '#3F6EB726',
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        width: '78px',
                                        padding: '6px 0 6px 16px !important',
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        gap={3}
                                        alignItems="center"
                                    >
                                        <Image
                                            title={
                                                row.sellerType === 'seller'
                                                    ? 'Seller'
                                                    : 'Service Provider'
                                            }
                                            data-testclass="vendor-type"
                                            src={
                                                row.sellerType === 'seller'
                                                    ? String(SellerIcon)
                                                    : String(ServiceIcon)
                                            }
                                            style={{
                                                aspectRatio: '1/1',
                                                width: '12px',
                                                backgroundColor: 'inherit',
                                            }}
                                        />
                                        {row?.logo ? (
                                            <Image
                                                data-testclass="vendor-logo"
                                                src={row.logo}
                                                style={{
                                                    aspectRatio: '1/1',
                                                    height: '32px',
                                                    border: '1px solid #E9E8E8',
                                                    borderRadius: '4px',
                                                }}
                                                imageStyle={{
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '32px',
                                                    height: '32px',
                                                    backgroundColor: '#808080',
                                                    color: '#FFFFFF',
                                                    borderRadius: '4px',
                                                    fontSize: '1.6rem',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {row.title
                                                    .charAt(0)
                                                    .toUpperCase()}
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        data-testclass="vendor-name"
                                    >
                                        {row.title}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        data-testclass="vendor-specialties"
                                    >
                                        {row.speciality?.length <= 2
                                            ? row.speciality.join(', ')
                                            : `${row.speciality?.length} specialties`}
                                    </Typography>
                                </TableCell>
                                {isMainVendorsPage ? (
                                    <>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                data-testclass="vendor-primary-contact"
                                            >
                                                {row.primaryContact}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                boxSizing: 'border-box',
                                                maxWidth: '240px',
                                            }}
                                        >
                                            {row?.phone && (
                                                <Typography
                                                    variant="body2"
                                                    data-testclass="vendor-phone"
                                                >
                                                    {formatPhoneNumber(
                                                        row.phone,
                                                    )}
                                                </Typography>
                                            )}
                                            <Typography
                                                data-testclass="vendor-email"
                                                color="#5492F4"
                                                sx={{
                                                    display: 'block',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                href={`mailto:${row.email}`}
                                                component={Link}
                                                title={row.email}
                                                variant={
                                                    row?.phone
                                                        ? 'caption'
                                                        : 'body2'
                                                }
                                            >
                                                {row.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                component="div"
                                                variant="body2"
                                                data-testclass="vendor-product-ordering"
                                            >
                                                <VendorAccountStatusPill
                                                    vendor={row}
                                                    showDashOnEmptyStatus={true}
                                                />
                                            </Typography>
                                        </TableCell>
                                    </>
                                ) : (
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            data-testclass="vendor-status"
                                        >
                                            {row.isAddToProperty
                                                ? 'Added'
                                                : 'Not Added'}
                                        </Typography>
                                    </TableCell>
                                )}
                                {isMainVendorsPage && onDeleteVendor && (
                                    <TableCell align="center">
                                        <Button
                                            data-testclass="btn-vendor-delete"
                                            sx={{
                                                '& span': {
                                                    margin: 0,
                                                },
                                                padding: '5px',
                                                minWidth: 'auto',
                                            }}
                                            startIcon={
                                                <DeleteOutline
                                                    color={
                                                        deleteVendorId ===
                                                        row.id
                                                            ? 'primary'
                                                            : 'disabled'
                                                    }
                                                    sx={{
                                                        fontSize:
                                                            '2.5rem !important',
                                                    }}
                                                />
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteVendor(row);
                                            }}
                                        />
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
